import Vue from 'vue';

let _default: Conn | null = null;

export default class Conn {
  static url: string | (() => string) | undefined;

  static base = '/';

  static requestInit: (() => RequestInit) | undefined;

  static get default(): Conn {
    if (!_default) {
      _default = new Conn();
    }
    return _default;
  }

  constructor(options?: {
    url?: string | (() => string);
    base?: string;
    requestInit?: () => RequestInit;
  }) {
    const { url, base, requestInit } = options || {};
    this._url = url;
    this._base = base;
    this._requestInit = requestInit;
    this.checkConnection();
  }

  private _url: string | (() => string) | undefined;

  private _base: string | undefined;

  private _requestInit: (() => RequestInit) | undefined;

  private _store = Vue.observable({
    isOn: null as boolean | null,
    lastCheck: null as number | null,
  });

  get isOn() {
    return this._store.isOn;
  }

  get isOff() {
    return !this.isOn;
  }

  async checkConnection() {
    try {
      this._checkNavigator();
      const isOn = this._checkTimestamp();
      if (isOn != null) {
        return;
      }
      await this._fetch();
      this._store.lastCheck = this._getTimestamp();
      this._store.isOn = true;
    } catch (err) {
      if (err instanceof TypeError) {
        this._store.lastCheck = this._getTimestamp();
        this._store.isOn = false;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Error while checking connection:', err);
        }
        // ignore other errors
      }
    }
  }

  async checkIsOnline() {
    await this.checkConnection();
    return this.isOn === true;
  }

  async checkIsOffline() {
    await this.checkConnection();
    return this.isOn === false;
  }

  private _checkNavigator() {
    if (window.navigator.onLine === false) {
      throw new TypeError('Offline');
    }
  }

  private _checkTimestamp() {
    const bufferMs = 1000;
    const { isOn, lastCheck } = this._store;
    if (
      isOn != null &&
      lastCheck &&
      this._getTimestamp() - lastCheck < bufferMs
    ) {
      return isOn;
    }
    return null;
  }

  private async _fetch() {
    const url = this._getUrl();
    if (!url) {
      throw new Error('No Conn.url');
    }
    const requestInit: RequestInit = {
      cache: 'no-cache',
      credentials: 'omit',
      ...(this._requestInit ?? Conn.requestInit)?.call(null),
    };
    const res = await fetch(url, requestInit);
    if (!res.ok) {
      throw new Error(
        `${res.status}: ${res.statusText || 'Connection check error'}`,
      );
    }
  }

  private _getTimestamp() {
    return new Date().valueOf();
  }

  private _getUrl() {
    let url: string | null = null;
    const _url = this._url ?? Conn.url;
    if (typeof _url === 'string') {
      url = _url;
    } else if (typeof _url === 'function') {
      url = _url();
    }
    if (url == null) {
      const timestamp = +new Date();
      let base = this._base ?? Conn.base;
      if (!base.endsWith('/')) {
        base = `${base}/`;
      }
      url = `${base}manifest.json?_=${timestamp}`;
    }
    return url;
  }
}

try {
  Conn.base = process.env.BASE_URL || '/';
} catch (err) {
  // We ignore the error
}
